interface RawInstagramReel {
  url?: string;
  caption?: string;
  transcript?: string;
  hashtags?: string[];
  likesCount?: number;
  commentsCount?: number;
  viewCount?: number;
  durationSeconds?: number;
  timestamp?: string;
  displayUrl?: string;
  musicArtist?: string;
  musicTitle?: string;
  usesOriginalAudio?: boolean;
}

interface InstagramReelsSource {
  customFields?: Record<string, unknown> | null;
  contacts?: Array<{
    name?: string | null;
    customFields?: Record<string, unknown> | null;
  }> | null;
}

/**
 * A reel as the lead detail renders it. Distinct from a post because it carries
 * the two things a photo post cannot: what the person actually SAID (the
 * transcript) and how many people watched.
 */
export interface InstagramReelView {
  url: string;
  caption: string;
  transcript?: string;
  likesCount: number;
  commentsCount: number;
  viewCount?: number;
  durationSeconds?: number;
  timestamp: string;
  displayUrl?: string;
  /** Formatted credit, absent when the reel uses the person's own audio. */
  music?: string;
  usesOriginalAudio?: boolean;
}

function mapReel(reel: RawInstagramReel): InstagramReelView {
  const hashtagLine = reel.hashtags?.length
    ? reel.hashtags.map((tag) => `#${tag}`).join(' ')
    : undefined;

  const music =
    !reel.usesOriginalAudio && (reel.musicArtist || reel.musicTitle)
      ? [reel.musicArtist, reel.musicTitle].filter(Boolean).join(' — ')
      : undefined;

  return {
    url: reel.url || '',
    caption: [reel.caption, hashtagLine].filter(Boolean).join('\n\n'),
    transcript: reel.transcript?.trim() ? reel.transcript : undefined,
    likesCount: reel.likesCount ?? 0,
    commentsCount: reel.commentsCount ?? 0,
    viewCount: reel.viewCount,
    durationSeconds: reel.durationSeconds,
    timestamp: reel.timestamp ?? '',
    displayUrl: reel.displayUrl,
    ...(music && { music }),
    ...(reel.usesOriginalAudio !== undefined && { usesOriginalAudio: reel.usesOriginalAudio }),
  };
}

function reelsFrom(customFields: Record<string, unknown> | null | undefined): RawInstagramReel[] {
  const reels = customFields?.instagramReels;
  return Array.isArray(reels) ? (reels as RawInstagramReel[]) : [];
}

/**
 * Gathers the reels the enrichment pipeline persisted, from the lead and from any
 * of its contacts, newest first.
 *
 * Mirrors `collectInstagramPosts`. It exists because the pipeline has been
 * storing reels — transcripts and all — that nothing in the UI ever read.
 */
export function collectInstagramReels(lead: InstagramReelsSource): InstagramReelView[] {
  const leadReels = reelsFrom(lead.customFields).map(mapReel);
  const contactReels = (lead.contacts ?? []).flatMap((contact) =>
    reelsFrom(contact.customFields).map(mapReel),
  );

  const seen = new Set<string>();
  const merged: InstagramReelView[] = [];
  for (const reel of [...leadReels, ...contactReels]) {
    const key = reel.url || reel.caption;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(reel);
  }

  return merged.sort(
    (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime(),
  );
}
